import { VerifyMethodType } from "shared/types";
import {
  CombinedIncome,
  VerificationSpecificAdditionaData
} from "./types/income";
import { CombinedIncomeReport } from "./types";
import { VerifiedItem } from "./types/verified";

export const incomeTypeMap = {
  self_employment: "Self-employment",
  employment: "Employment",
  other: "Other",
  housing_assistance: "Housing assistance",
  offer_letter: "Offer letter"
};

export const verificationSourceType = {
  [VerifyMethodType.Bank]: "Bank account",
  [VerifyMethodType.Paystub]: "Paystub",
  [VerifyMethodType.BankStatement]: "Bank statement",
  [VerifyMethodType.Payroll]: "Payroll provider",
  [VerifyMethodType.OfferLetter]: "Offer letter"
};

export const getVerificationTitleName = (income: CombinedIncome) => {
  switch (income.type) {
    case "employment":
      return income.additional_data.company_name || "Employment";
    case "self_employment":
      return income.additional_data.company_name || "Self-employment";
    case "other":
      return income.additional_data.paid_from || "Other";
    case "housing_assistance":
      return "Housing assistance";
    case "offer_letter":
      return income.additional_data?.company_name || "Offer letter";
    default:
      return "Unknown income type";
  }
};

export const getIncomeJobName = (income: CombinedIncome) => {
  if (!income.additional_data) return "Unknown";

  if (
    "company_name" in income.additional_data &&
    income.additional_data.company_name
  )
    return income.additional_data.company_name;

  if ("job_title" in income.additional_data && income.additional_data.job_title)
    return income.additional_data.job_title;

  if (
    "description" in income.additional_data &&
    income.additional_data.description
  )
    return income.additional_data.description;

  if ("paid_from" in income.additional_data && income.additional_data.paid_from)
    return income.additional_data.paid_from;

  return "Unknown";
};

export const getMonthlyAmount = (
  amountCents: number,
  payPeriod: string
): number => {
  switch (payPeriod) {
    case "weekly":
      return (amountCents * 52) / 12;
    case "bi_weekly":
      return (amountCents * 26) / 12;
    case "annually":
      return amountCents / 12;
    default:
      return amountCents; // Assuming monthly if not specified
  }
};

export const isIncomeVerificationSkipped = (
  income: CombinedIncome
): boolean => {
  const additionalData = income.additional_data;
  const hasSkippedFlag = Boolean(
    additionalData &&
    "document_verification_skipped" in additionalData &&
    additionalData.document_verification_skipped
  );
  const hasNoVerificationData = !income.items.length;

  return Boolean(hasSkippedFlag && hasNoVerificationData);
};

export const getAdditionalDataPerVerificationType = (
  report?: CombinedIncomeReport,
  income?: CombinedIncome
): VerificationSpecificAdditionaData | null => {
  if (!report || !income?.verified_source_type) return null;

  const additionalVerificationData: VerificationSpecificAdditionaData = {};

  if (report.bank_statements)
    additionalVerificationData[VerifyMethodType.BankStatement] = {
      allBankStatements: report.bank_statements,
      companyName: getIncomeJobName(income),
      incomeId: income.id
    };

  return additionalVerificationData;
};

export const getIncomeOriginalNetAndGrossDollars = (
  income: CombinedIncome,
  currentItem?: VerifiedItem | VerifiedItem[]
) => {
  let originalNetMonthlyIncomeDollars =
    income.verified_monthly_amounts.net || 0;
  let originalGrossMonthlyIncomeDollars =
    income.verified_monthly_amounts.gross || 0;

  if (currentItem && !Array.isArray(currentItem) && currentItem.data) {
    if ("net_monthly_income" in currentItem.data)
      originalNetMonthlyIncomeDollars =
        (currentItem.data.net_monthly_income?.cents || 0) / 100;
    else if ("net_monthly_income_amount" in currentItem.data)
      originalNetMonthlyIncomeDollars =
        currentItem.data.net_monthly_income_amount || 0;

    if ("gross_monthly_income" in currentItem.data)
      originalGrossMonthlyIncomeDollars =
        (currentItem.data.gross_monthly_income?.cents || 0) / 100;
    else if ("gross_monthly_income_amount" in currentItem.data)
      originalGrossMonthlyIncomeDollars =
        currentItem.data.gross_monthly_income_amount || 0;
  }

  return { originalNetMonthlyIncomeDollars, originalGrossMonthlyIncomeDollars };
};

export const getIncomeActualNetAndGrossDollars = (
  income: CombinedIncome,
  currentItem?: VerifiedItem | VerifiedItem[]
) => {
  const { originalNetMonthlyIncomeDollars, originalGrossMonthlyIncomeDollars } =
    getIncomeOriginalNetAndGrossDollars(income, currentItem);

  const actualVerifiedNetMonthly =
    income.additional_data?.overrides?.net_monthly_income !== undefined
      ? income.additional_data.overrides.net_monthly_income
      : originalNetMonthlyIncomeDollars;

  const actualVerifiedGrossMonthly =
    income.additional_data?.overrides?.gross_monthly_income !== undefined
      ? income.additional_data.overrides.gross_monthly_income
      : originalGrossMonthlyIncomeDollars;

  return {
    actualVerifiedNetMonthly,
    actualVerifiedGrossMonthly
  };
};
